import type { GameState } from "../sim/types";

/**
 * Cliente del leaderboard.
 *
 * El cliente nunca escribe en la tabla: manda el envio a una Edge Function que
 * recalcula las cotas de puntaje, duracion y teclas, y decide si es posible.
 * Ver supabase/functions/submit-score/index.ts.
 */

const URL_BASE = import.meta.env.VITE_SUPABASE_URL ?? "";
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";
const PENDING_KEY = "modulo:pending";
const NAME_KEY = "modulo:name";
const BEST_KEY = "modulo:best";

export interface Entry {
  rank: number;
  name: string;
  score: number;
  wave: number;
  band_max: number;
  platform: string;
  created_at: string;
}

export interface Payload {
  name: string;
  score: number;
  wave: number;
  bandMax: number;
  durationMs: number;
  keystrokes: number;
  platform: "web" | "mobile";
}

export const configured = (): boolean => URL_BASE.length > 0 && ANON.length > 0;

export const savedName = (): string => localStorage.getItem(NAME_KEY) ?? "";
export const rememberName = (n: string): void => localStorage.setItem(NAME_KEY, n);

export function localBest(): number {
  return Number(localStorage.getItem(BEST_KEY) ?? "0");
}
export function setLocalBest(v: number): void {
  if (v > localBest()) localStorage.setItem(BEST_KEY, String(v));
}

export function buildPayload(s: GameState, name: string): Payload {
  return {
    name,
    score: s.score,
    wave: s.wave,
    // hardestRun, no hardestWave: el segundo se reinicia en cada ola
    bandMax: s.hardestRun,
    durationMs: Math.max(1000, Math.round((s.endedAt || performance.now()) - s.startedAt)),
    keystrokes: s.keystrokes,
    platform: matchMedia("(pointer: coarse)").matches ? "mobile" : "web",
  };
}

async function post(p: Payload): Promise<{ ok: true; rank: number } | { ok: false; message: string }> {
  const res = await fetch(`${URL_BASE}/functions/v1/submit-score`, {
    method: "POST",
    headers: { "content-type": "application/json", apikey: ANON },
    body: JSON.stringify(p),
  });
  const data = (await res.json().catch(() => ({}))) as { rank?: number; error?: string };
  if (!res.ok) return { ok: false, message: data.error ?? "No se pudo enviar" };
  return { ok: true, rank: data.rank ?? 0 };
}

/** Si falla, guarda el envio y lo reintenta al abrir. Un puntaje perdido por
 *  wifi malo se siente peor que un leaderboard imperfecto. */
export async function submit(
  p: Payload,
): Promise<{ ok: true; rank: number } | { ok: false; message: string }> {
  setLocalBest(p.score);
  if (!configured()) return { ok: false, message: "Leaderboard sin configurar" };
  try {
    return await post(p);
  } catch {
    const q = JSON.parse(localStorage.getItem(PENDING_KEY) ?? "[]") as Payload[];
    q.push(p);
    localStorage.setItem(PENDING_KEY, JSON.stringify(q.slice(-5)));
    return { ok: false, message: "Sin conexion, se reintentara" };
  }
}

export async function flushPending(): Promise<void> {
  if (!configured()) return;
  const q = JSON.parse(localStorage.getItem(PENDING_KEY) ?? "[]") as Payload[];
  if (!q.length) return;
  const left: Payload[] = [];
  for (const p of q) {
    try {
      const r = await post(p);
      if (!r.ok) left.push(p);
    } catch {
      left.push(p);
    }
  }
  localStorage.setItem(PENDING_KEY, JSON.stringify(left));
}

export async function fetchTop(limit = 10): Promise<Entry[]> {
  if (!configured()) return [];
  try {
    const res = await fetch(
      `${URL_BASE}/rest/v1/leaderboard_top?select=*&limit=${limit}`,
      { headers: { apikey: ANON } },
    );
    return res.ok ? ((await res.json()) as Entry[]) : [];
  } catch {
    return [];
  }
}
